export interface InquiryRow {
  creatorUserId?: string | null;
  creator?: {
    id?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    photoURL?: string | null;
    instaHandle?: string | null;
    instaFollowers?: number | null;
    tikTokHandle?: string | null;
    tikTokFollowers?: number | null;
  } | null;
  publishedContent?: { socialPostId?: string | null }[] | null;
}

/** A creator added to the monitoring brief. `posts` counts what Noodle found on their accounts for this brief. */
export interface MonitorCreator {
  id: string;
  name: string;
  handle: string;
  followers: number;
  photoUrl: string | null;
  posts: number;
}

/**
 * One entry per creator, in inquiry order. A creator with two inquiries keeps the first and adds the posts.
 * Imported creators carry a placeholder `_upload` last name, which is not part of their name.
 */
export function briefCreators(inquiries: readonly InquiryRow[]): MonitorCreator[] {
  const byId = new Map<string, MonitorCreator>();
  for (const inquiry of inquiries) {
    const creator = inquiry.creator;
    const id = creator?.id?.trim() || inquiry.creatorUserId?.trim() || '';
    if (!id) {
      continue;
    }
    const posts = (inquiry.publishedContent ?? []).filter((post) => !!post.socialPostId?.trim()).length;
    const existing = byId.get(id);
    if (existing) {
      existing.posts += posts;
      continue;
    }
    const handle = presentHandle(creator?.tikTokHandle) || presentHandle(creator?.instaHandle);
    const name = [creator?.firstName, creator?.lastName]
      .map((part) => part?.trim() ?? '')
      .filter((part) => part.length > 0 && part !== '_upload')
      .join(' ');
    byId.set(id, {
      id,
      name: name || handle || 'Creator',
      handle,
      followers: Math.max(num(creator?.tikTokFollowers), num(creator?.instaFollowers)),
      photoUrl: creator?.photoURL?.trim() || null,
      posts,
    });
  }
  return [...byId.values()];
}

function presentHandle(value: string | null | undefined): string {
  const trimmed = value?.trim().replace(/^@/, '') ?? '';
  return trimmed ? `@${trimmed}` : '';
}

function num(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}
